import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MonthlyView from "./components/MonthlyView";
import AddVideoModal from "./components/AddVideoModal";
import { ReactComponent as AddPlaylistIcon } from "../../assets/Icons/addtoplaylist.svg";
import {
  listVideosByDate,
  deleteVideoFromCalendar,
  updateVideoComplete,
} from "../../network/service";
import { getUser } from "../../Redux/UserSlice/UserSlice";
import { toast } from "react-toastify";
import Translate from "../../Components/Multilanguage/Translate";

const Calendar = () => {
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const isUpdatingRef = useRef(false);
  const monthRef = useRef(null);
  const yearRef = useRef(null);
  const currentMonthYearRef = useRef(null);
  const dispatch = useDispatch();

  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const user = useSelector((state) => state?.user?.value);

  const appInfo = useMemo(
    () => ({
      projectDetails: projectInfo,
      accessToken: accessToken,
    }),
    [projectInfo, accessToken]
  );

  const template = projectInfo?.projectConfig?.config?.TEMPLATE;

  useEffect(() => {
    if (isUpdatingRef.current) return;

    const queryParams = new URLSearchParams(location.search);
    const dateParam = queryParams.get("date");

    if (dateParam) {
      const newDate = new Date(dateParam);
      if (!isNaN(newDate.getTime())) {
        setDate(newDate);
      }
    }
  }, [location]);

  useEffect(() => {
    const monthYearKey = `${date.getMonth()}-${date.getFullYear()}`;

    if (monthYearKey !== currentMonthYearRef.current) {
      currentMonthYearRef.current = monthYearKey;
      fetchCalendarData(date, true);
    }
  }, [date]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchCalendarData = async (selectedDate, showLoader = true) => {
    try {
      setError(null);
      if(showLoader) setLoading(true);

      const firstDayOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        1
      );
      const dayOfWeek = firstDayOfMonth.getDay();

      const firstVisibleDate = new Date(firstDayOfMonth);
      firstVisibleDate.setDate(firstVisibleDate.getDate() - dayOfWeek);

      const lastDayOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0
      );
      const lastDayOfWeek = lastDayOfMonth.getDay();

      const lastVisibleDate = new Date(lastDayOfMonth);
      lastVisibleDate.setDate(lastVisibleDate.getDate() + (6 - lastDayOfWeek));
      const formattedStartDate = formatDateForApi(firstVisibleDate);
      const formattedEndDate = formatDateForApi(lastVisibleDate);

      const response = await listVideosByDate(appInfo, formattedStartDate, formattedEndDate);
      
      if (response?.data?.success) {
        const formattedEvents = [];
        const watchedDates = {};
        response?.data?.data?.forEach((day) => {
          if (day?.watched) {
            watchedDates[day?.date] = true;
          }
        });
        
        response.data.data.forEach((day) => {
          const eventDate = day.date ? new Date(day.date) : selectedDate;

          if (day.videos.length === 0 && day.watched) {
            formattedEvents.push({
              start: eventDate,
              watched: true,
              dateString: day.date,
            });
          }

          day.videos.forEach((event) => {
            formattedEvents.push({
              id: event.video_id,
              start: eventDate,
              title: event.title,
              description: event.description,
              image: event.thumbnail,
              duration: event.duration_text,
              completed: event.completed,
              order: event.order,
              calendar_id: event.calendar_id,
              partner: event.partner,
              is_user_video: event.is_user_video,
              watched: day.watched || false,
              dateString: day.date,
            });
          });
        });

        formattedEvents.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return 0;
        });
        Object.keys(watchedDates).forEach(dateString => {
          const hasEvents = formattedEvents.some(e => e.dateString === dateString);
          if (!hasEvents) {
            const eventDate = new Date(dateString);
            formattedEvents.push({
              start: eventDate,
              dateString: dateString,
              watched: true
            });
          }
        });

        formattedEvents.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return 0;
        });

        setEvents(formattedEvents);
      } else {
        setError("Failed to fetch calendar data");
      }
    } catch (err) {
      setError("An error occurred while fetching calendar data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateForUrl = (date) => {
    return formatDateForApi(date);
  };

  const navigateWithDate = (newDate) => {
    isUpdatingRef.current = true;
    setDate(newDate);
    navigate(`?date=${formatDateForUrl(newDate)}`);
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 50);
  };

  const handleDateChange = (newDate) => {
    navigateWithDate(newDate);
  };

  const handleTodayClick = () => {
    navigateWithDate(new Date());
  };

  const handlePreviousMonth = () => {
    const today = new Date();
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    // navigateWithDate(prevMonth);
    if (
      prevMonth.getFullYear() === today.getFullYear() &&
      prevMonth.getMonth() === today.getMonth()
    ) {
      navigateWithDate(new Date(today));
    } else {
      navigateWithDate(prevMonth);
    }
  };

  const handleNextMonth = () => {
    const today = new Date();
    const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    // navigateWithDate(nextMonth);
    if (
      nextMonth.getFullYear() === today.getFullYear() &&
      nextMonth.getMonth() === today.getMonth()
    ) {
      navigateWithDate(new Date(today));
    } else {
      navigateWithDate(nextMonth);
    }
  };

  const handleMonthChange = (monthIndex) => {
    const currentMonth = date.getMonth();
    const today = new Date();
    let newDate;
    // const newDate = new Date(date.getFullYear(), monthIndex, 1);

    if (
      date.getFullYear() === today.getFullYear() &&
      monthIndex === today.getMonth()
    ) {
      newDate = new Date(today);
    } else {
      newDate = new Date(date.getFullYear(), monthIndex, 1);
    }
    navigateWithDate(newDate);
    setShowMonthDropdown(false);
  };

  const handleYearChange = (year) => {
    // const newDate = new Date(year, date.getMonth(), 1);
    const today = new Date();
    let newDate;
    if (
      year === today.getFullYear() &&
      date.getMonth() === today.getMonth()
    ) {
      newDate = new Date(today);
    } else {
      newDate = new Date(year, date.getMonth(), 1);
    }
    navigateWithDate(newDate);
    setShowYearDropdown(false);
  };

  const handleDeleteVideo = async (calendarId) => {
    try {
      const response = await deleteVideoFromCalendar(appInfo, calendarId);
      if (response?.data?.success) {
        currentMonthYearRef.current = null;
        fetchCalendarData(date, false);
        toast.success(<Translate textKey={"video_deleted"} />);
      }
    } catch (err) {
      toast.error(<Translate textKey={"video_delete_failed"} />);
      console.error("Error deleting video:", err);
    }
  };

  const handleVideoComplete = async (calendarId, completed) => {
    try {
      const response = await updateVideoComplete(
        appInfo,
        calendarId,
        completed
      );
      if (response?.data?.success) {
        fetchCalendarData(date, false);
        toast.success(
          completed ? <Translate textKey={"video_complete"} /> : <Translate textKey={"video_incomplete"} />
        );
      }
    } catch (err) {
      toast.error(<Translate textKey={"video_update_failed"} />);
      console.error("Error deleting video:", err);
    }
  };

  const today = new Date();
  const currentYear = today.getFullYear();
  today.setHours(0, 0, 0, 0);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = [currentYear, currentYear + 1];

  const redirectToLogin = () => {
    dispatch(
      getUser({
        user: null,
      })
    );
    localStorage.removeItem("userId");
    navigate(projectInfo?.projectConfig?.config ?.WEBSITE_NEW_CHECKOUT_REQUIRED == "true" ?"/checkout": "/login", {
      state: { path: location?.pathname ,activeLogin: true },
    });
  };

  return (
    <div
      className={`calendar-container ${
        template !== "TEMPLATE_10" ? "not-gaia" : ""
      }`}
    >
      {error && <div className="error-message">{error}</div>}
      <div className="calendar">
        <div className="calendar-header">
          <div className="month-heading-container">
            <span className="prev-icon" onClick={handlePreviousMonth}>
              &#x1F870;
            </span>

            <div
              className={`month-year-selector ${
                template === "TEMPLATE_10" ? "gaia" : ""
              }`}
            >
              <div className="dropdown-wrapper" ref={monthRef}>
                <span
                  className="month-text"
                  onMouseOver={() => setShowMonthDropdown(true)}
                  onMouseLeave={() => setShowMonthDropdown(false)}
                >
                  {date.toLocaleString("default", { month: "long" })}
                </span>

                {showMonthDropdown && (
                  <div
                    className="dropdown months-dropdown"
                    onMouseOver={() => setShowMonthDropdown(true)}
                    onMouseLeave={() => setShowMonthDropdown(false)}
                  >
                    {months.map((month, index) => {
                      return (
                        <div
                          key={index}
                          className={`dropdown-item 
                            ${index === date.getMonth() ? "active" : ""}`}
                          onClick={() => handleMonthChange(index)}
                        >
                          {month}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="dropdown-wrapper" ref={yearRef}>
                <span
                  className="year-text"
                  onMouseOver={() => setShowYearDropdown(true)}
                  onMouseLeave={() => setShowYearDropdown(false)}
                >
                  {date.getFullYear()}
                </span>

                {showYearDropdown && (
                  <div
                    className="dropdown years-dropdown"
                    onMouseOver={() => setShowYearDropdown(true)}
                    onMouseLeave={() => setShowYearDropdown(false)}
                  >
                    {years.map((year) => (
                      <div
                        key={year}
                        className={`dropdown-item ${
                          year === date.getFullYear() ? "active" : ""
                        }`}
                        onClick={() => handleYearChange(year)}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <span className="next-icon" onClick={handleNextMonth}>
              &#x1F872;
            </span>
          </div>
          {date.setHours(0, 0, 0, 0) >= today.getTime() && (
            <button
              className={`add-video-btn ${template !== "TEMPLATE_10" ? "not-gaia" : ""}`}
              onClick={() => {
                if (!user) {
                  redirectToLogin();
                } else {
                  setShowAddVideoModal(true);
                }
              }}
            >
              <AddPlaylistIcon /> <Translate textKey={"add_video"} />
            </button>
          )}
          <button className="today-button" onClick={handleTodayClick}>
            <Translate textKey={"today"} />
          </button>
        </div>
        <div className="calendar-body">
          <MonthlyView
            events={events}
            date={date}
            onDateChange={handleDateChange}
            onDeleteVideo={handleDeleteVideo}
            onVideoComplete={handleVideoComplete}
            appInfo={appInfo}
            setShowAddVideoModal={setShowAddVideoModal}
            user={user}
            redirectToLogin={redirectToLogin}
            loading={loading}
          />
        </div>
        {showAddVideoModal && (
          <AddVideoModal
            appInfo={appInfo}
            date={date}
            onClose={() => setShowAddVideoModal(false)}
            onVideoAdded={() => {
              setShowAddVideoModal(false);
              fetchCalendarData(date);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Calendar;
